import React, { useState } from "react";
import { CreditCard, Gift, Loader2, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { money } from "../../lib/registry";

const key = (slug) => `lif_basket_${slug}`;

export function useBasket(slug) {
  const [items, setItems] = useState(() => { try { return JSON.parse(localStorage.getItem(key(slug)) || "[]"); } catch { return []; } });
  const save = (next) => { setItems(next); localStorage.setItem(key(slug), JSON.stringify(next)); };
  return {
    items,
    add: (it, amount) => save([...items.filter((i) => i.item_id !== it.item_id), { item_id: it.item_id, name: it.name, amount: Number(amount) }]),
    remove: (id) => save(items.filter((i) => i.item_id !== id)),
    clear: () => save([]),
  };
}

export function CardGiveForm({ it, onAdd }) {
  const [amount, setAmount] = useState("");
  return (
    <form onSubmit={(e) => { e.preventDefault(); onAdd(it, amount); }} className="flex gap-2" data-testid="give-card-form">
      <input required type="number" min="1" max="100000" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount $" className="input-luxe" data-testid="give-card-amount" />
      <button className="btn-gold whitespace-nowrap !px-4" data-testid="give-card-add"><Gift className="w-4 h-4" /> Add to basket</button>
    </form>
  );
}

function CheckoutDialog({ slug, basket, onClose }) {
  const [f, setF] = useState({ name: "", email: "", note: "" });
  const [busy, setBusy] = useState(false);
  const total = basket.items.reduce((s, i) => s + i.amount, 0);
  async function pay(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/public/weddings/${slug}/registry/checkout`, { ...f, items: basket.items.map(({ item_id, amount }) => ({ item_id, amount })), origin_url: window.location.origin });
      basket.clear();
      window.location.href = data.url;
    } catch (err) { toast.error(formatApiError(err)); setBusy(false); }
  }
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="basket-dialog">
        <div className="flex justify-between items-start mb-4"><div><p className="font-script text-[var(--gold)] text-2xl">Gift basket</p><h3 className="font-serif text-2xl">Pay by card</h3></div><button onClick={onClose} data-testid="basket-close"><X className="w-5 h-5" /></button></div>
        <div className="space-y-2 mb-4">
          {basket.items.map((i) => (
            <div key={i.item_id} className="flex items-center justify-between gap-3 border border-[var(--line)] rounded-xl px-4 py-2.5 text-sm" data-testid={`basket-item-${i.item_id}`}>
              <span className="truncate">{i.name}</span><span className="flex items-center gap-3"><b>${money(i.amount)}</b><button type="button" onClick={() => basket.remove(i.item_id)} aria-label="Remove" data-testid={`basket-remove-${i.item_id}`}><Trash2 className="w-4 h-4 text-[var(--muted)]" /></button></span>
            </div>
          ))}
        </div>
        <form onSubmit={pay} className="space-y-2">
          <input required maxLength={80} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Your name" className="input-luxe" data-testid="basket-name" />
          <input required type="email" maxLength={120} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Your email (for your receipt)" className="input-luxe" data-testid="basket-email" />
          <textarea rows={2} maxLength={300} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="A note for the couple (optional)" className="input-luxe resize-none" data-testid="basket-note" />
          <button disabled={busy || !basket.items.length} className="btn-gold w-full justify-center" data-testid="basket-pay">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Pay ${money(total)}</button>
          <p className="text-[11px] text-[var(--muted)] text-center">Secure card payment. You get an emailed receipt and PDF invoice. Test card 4242 4242 4242 4242.</p>
        </form>
      </div>
    </div>
  );
}

export function BasketBar({ slug, basket }) {
  const [open, setOpen] = useState(false);
  if (!basket.items.length) return null;
  const total = basket.items.reduce((s, i) => s + i.amount, 0);
  return (
    <>
      <button onClick={() => setOpen(true)} className="fixed bottom-5 left-1/2 -translate-x-1/2 z-40 bg-[var(--ink)] text-white rounded-full pl-5 pr-2 py-2 flex items-center gap-4 shadow-xl" data-testid="basket-bar">
        <span className="text-sm"><Gift className="w-4 h-4 inline text-[var(--gold)] mr-1.5" />{basket.items.length} gift{basket.items.length > 1 ? "s" : ""} · ${money(total)}</span>
        <span className="bg-[var(--gold)] rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.16em]">Checkout</span>
      </button>
      {open && <CheckoutDialog slug={slug} basket={basket} onClose={() => setOpen(false)} />}
    </>
  );
}
